import React, { useRef, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { isOriginInUse } from '../../../util/search';

import { Form, IconLocation, LocationAutocompleteInput } from '../../../components';

import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

import css from './SearchLocationPrimary.module.css';

const identity = v => v;

const CustomIconLocation = () => {
  return <IconLocation rootClassName={css.customIconLocation} />;
};

const SearchIconButton = props => {
  const { intl } = props;

  return (
    <button
      className={css.searchButton}
      type="submit"
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
    >
      <svg
        className={css.searchIcon}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        xmlns="http://www.w3.org/2000/svg"
        role="none"
      >
        <g
          fill="none"
          fillRule="evenodd"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.6"
        >
          <path className={css.searchIconStroke} d="M10.4 10.9L14 14.5" />
          <circle className={css.searchIconStroke} cx="6.6" cy="6.6" r="5.1" />
        </g>
      </svg>
    </button>
  );
};

const getLocationSearchInitialValues = ({ address, origin, bounds }, config) => {
  const locationFieldsPresent = isOriginInUse(config)
    ? address && origin && bounds
    : address && bounds;

  return locationFieldsPresent
    ? {
        location: {
          search: address,
          selectedPlace: { address, origin, bounds },
        },
      }
    : {};
};

const LocationField = props => {
  const { inputRef, intl, isCurrentLocation, onLocationChange } = props;

  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <div className={css.fieldShell}>
            <LocationAutocompleteInput
              id="search-page-primary-location"
              className={css.field}
              useDarkText
              inputClassName={classNames(css.input, {
                [css.inputWithCurrentLocation]: isCurrentLocation,
              })}
              predictionsClassName={css.predictions}
              CustomIcon={CustomIconLocation}
              iconClassName={css.locationAutocompleteInputIconWrapper}
              isCurrentLocation={isCurrentLocation}
              closeOnBlur
              placeholder={
                isCurrentLocation
                  ? intl.formatMessage({ id: 'PageBuilder.SearchCTA.currentLocationPlaceholder' })
                  : intl.formatMessage({ id: 'PageBuilder.SearchCTA.locationPlaceholder' })
              }
              inputRef={inputRef}
              input={{ ...restInput, onChange: searchOnChange }}
              meta={meta}
              ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
            />
            <SearchIconButton intl={intl} />
          </div>
        );
      }}
    />
  );
};

/**
 * Desktop location search for SearchPage primary filters.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {Object} props.config
 * @param {Object} props.currentSearchParams
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {Array} props.routeConfiguration
 * @returns {JSX.Element}
 */
const SearchLocationPrimary = props => {
  const intl = useIntl();
  const inputRef = useRef(null);
  const [isCurrentLocation, setIsCurrentLocation] = useState(false);
  const { className, config, currentSearchParams, history, location, routeConfiguration } = props;

  const handleSubmit = values => {
    const { location: locationValue } = values || {};
    const { search, selectedPlace } = locationValue || {};

    if (search?.length > 0 && !selectedPlace) {
      return;
    }

    const rest = { ...(currentSearchParams || {}) };
    delete rest.address;
    delete rest.bounds;
    delete rest.origin;

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    if (!search && !selectedPlace) {
      history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, rest));
      return;
    }

    const { origin, bounds } = selectedPlace || {};
    const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, {
        ...rest,
        address: search,
        bounds,
        ...originMaybe,
      })
    );
  };

  const handleLocationChange = (value, form) => {
    const selectedPlace = value?.selectedPlace;

    setIsCurrentLocation(!!(selectedPlace && selectedPlace.address === ''));

    if (selectedPlace) {
      form.submit();
      inputRef.current?.blur();
    }
  };

  const initialValues = getLocationSearchInitialValues(currentSearchParams, config);

  return (
    <div className={classNames(css.root, className)}>
      <FinalForm
        initialValues={initialValues}
        keepDirtyOnReinitialize={false}
        onSubmit={handleSubmit}
        render={({ form, handleSubmit }) => {
          return (
            <Form
              role="search"
              className={css.form}
              onSubmit={handleSubmit}
              enforcePagePreloadFor="SearchPage"
            >
              <LocationField
                inputRef={inputRef}
                intl={intl}
                isCurrentLocation={isCurrentLocation}
                onLocationChange={value => handleLocationChange(value, form)}
              />
            </Form>
          );
        }}
      />
    </div>
  );
};

export default SearchLocationPrimary;
